/* eslint react/destructuring-assignment: 0 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { HotKeys } from 'react-hotkeys';
import { isEmpty, sample, noop } from 'lodash';
import { isDirty, getFormValues } from 'redux-form';
import Preloader from '@omilia/preloader';
import Modal from '@omilia/modal';

import Notify from 'store/utils/Notify';

// FIXME: Drop once angular is gone
import _emitter, { C_RULES_CREATE_NEW_INTENT } from '_emitter';

import COMBOS from 'constants/keyboardShortcutsCombos';
import { transformBlocks } from 'store/utils/intents';
import * as TYPES from 'types';

import withPartialImportModal from 'components/withPartialImportModal';
import { withExportingModal } from 'components/PartialExportModal';
import Splitter from 'components/Splitter';
import UnitsSearchResult from 'components/Units/UnitsSearchResult';
import NoUnits from 'components/Units/NoUnits';
import UnsavedChangesModal from 'components/UnsavedChangesModal';
import Pagination from 'components/Pagination';
import SortingMenu from 'components/SortingMenu';
import FilterMenu from 'components/FilterMenu';
import ListHeaderOptions from 'components/ListHeaderOptions';
import FORM_NAMES from 'constants/formNames';

import * as actions from 'store/services/intents/actions';
import * as selectors from 'store/services/intents/selectors';
import * as projectSelectors from 'store/services/project/selectors';
import * as annotationsActions from 'store/services/annotations/actions';
import * as annotationsSelectors from 'store/services/annotations/selectors';
import * as colorsSelectors from 'store/services/colors/selectors';
import { updateUtterances as saveUtterances } from 'store/services/intentUtterances/actions';

import TestSets from 'scenes/TestSets';

import List from './components/IntentsList';
import Inspect from './components/IntentInspect';
import CreateModal from './components/CreateNewIntentModal';
import WinningPanel from './components/WinningIntentsPanel';

import getTrainModalContents from './utils/getTrainModalContents';

import './style.scss';

const HOTKEYS_MAP = {
  create: COMBOS.createNew,
};

const isShowingImportModal = (location, resource) =>
  !!matchPath(location.pathname, {
    exact: true,
    path: `/:company/:project/${resource}/import/:state`,
  });

export class Intents extends Component {
  handleHotKeys = {
    create: this.handleOpenCreateModal,
  };

  static propTypes = {
    annotatorHasSelectedResult: PropTypes.bool.isRequired,
    hasMachineLearningFeatureEnabled: PropTypes.bool.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    inspectIsDirty: PropTypes.bool.isRequired,
    isTraining: PropTypes.bool.isRequired,
    isTrainingError: PropTypes.string.isRequired,
    listIsLoading: PropTypes.bool.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
    match: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }).isRequired,
    partialExport: PropTypes.func.isRequired,
    train: PropTypes.func.isRequired,
    onAnnotatorDeselect: PropTypes.func.isRequired,
    onAnnotatorExecute: PropTypes.func.isRequired,

    onCheckIntentName: PropTypes.func.isRequired,
    onListGet: PropTypes.func.isRequired,
    onListItemAdd: PropTypes.func.isRequired,
    onListItemDeselect: PropTypes.func.isRequired,
    onListItemPartialUpdate: PropTypes.func.isRequired,
    onListItemSelect: PropTypes.func.isRequired,
    annotatorSelectedResult: TYPES.annotationType,

    colors: PropTypes.arrayOf(PropTypes.string),
    inspectingItem: TYPES.intentType,

    inspectingItemIndex: PropTypes.number,
    list: PropTypes.arrayOf(TYPES.intentType),

    listCount: PropTypes.number,
    listFilter: PropTypes.arrayOf(TYPES.filterOptionsType),

    listPagination: TYPES.paginationOptionsType,

    listSort: PropTypes.arrayOf(TYPES.sortOptionType),
    listSortSelected: TYPES.sortOptionType,

    project: TYPES.projectType,
  };

  state = {
    isCreateModalOpen: false,
    selectedTabIndex: 2,
    toDuplicate: null,
    trainingModalIsOpen: false,
    unsavedChangesModal: {
      isOpen: false,
      onDiscardCallback: noop,
    },
  };

  componentDidMount = () => {
    this.fetchAllItems();

    // FIXME: Drop once angular is gone
    _emitter.on(C_RULES_CREATE_NEW_INTENT, ({ constraintSets }) => {
      this.handleOpenCreateModal({
        name: '',
        description: '',
        constraintSets,
      });
    });
  };

  componentWillReceiveProps({ inspectingItem }) {
    // TODO: handle this weird logic
    if (
      !isShowingImportModal(this.props.location, 'intents') &&
      !isEmpty(this.props.inspectingItem) &&
      isEmpty(inspectingItem)
    ) {
      this.props.history.push(this.props.match.url);
    }
  }

  componentWillUnmount = () => {
    // FIXME: Drop once angular is gone
    _emitter.removeListener(
      C_RULES_CREATE_NEW_INTENT,
      this.handleOpenCreateModal,
    );
  };

  toggleIsTrainingModal = () =>
    this.setState(({ trainingModalIsOpen }) => ({
      trainingModalIsOpen: !trainingModalIsOpen,
    }));

  openUnsavedChangesModal = (onDiscardCallback = noop) =>
    this.setState(({ unsavedChangesModal }) => ({
      unsavedChangesModal: {
        ...unsavedChangesModal,
        isOpen: true,
        onDiscardCallback,
      },
    }));

  closeUnsavedChangesModal = () =>
    this.setState(({ unsavedChangesModal }) => ({
      unsavedChangesModal: {
        ...unsavedChangesModal,
        isOpen: false,
        onDiscardCallback: noop,
      },
    }));

  handleListItemAdd = async ({ intentName, description, constraintSets }) => {
    const { onListItemAdd, colors } = this.props;
    const id = await onListItemAdd({
      description,
      name: intentName,
      color: sample(colors),
      constraint_sets: transformBlocks(constraintSets),
    });

    if (id) {
      this.props.history.push(`${this.props.match.url}/${id}`);

      this.handleCloseCreateModal();
      this.handleChangeTab(2);
    }
  };

  handleOpenCreateModal = (toDuplicate = null) => {
    this.setState({
      toDuplicate,
      isCreateModalOpen: true,
    });
  };

  handleCloseCreateModal = () => {
    this.setState({
      isCreateModalOpen: false,
      toDuplicate: false,
    });
  };

  handleChangeTab = index => {
    this.setState({
      selectedTabIndex: index,
    });
  };

  handleFilter = params => {
    this.props.onListGet({ page: 1 }, params);
  };

  fetchAllItems = () => {
    this.props.onListGet({
      page: 1,
      name__icontains: '',
    });
  };

  handleListItemSelect = item => {
    const selectIntent = () => {
      this.props.onListItemSelect(item);
      this.props.history.push(`${this.props.match.url}/${item.id}`);
    };

    if (this.props.inspectIsDirty) {
      const onDiscardCallback = () => {
        this.closeUnsavedChangesModal();
        selectIntent();
      };
      this.openUnsavedChangesModal(onDiscardCallback);
    } else {
      selectIntent();
    }
  };

  handleListItemDuplicate = item => {
    this.handleOpenCreateModal({
      name: `${item.name}_copy`,
      description: item.description,
      constraintSets: item.constraint_sets,
    });
  };

  handleListItemToggleStatus = async (id, name, data) => {
    await this.props.onListItemPartialUpdate(id, data);

    if (data.enabled) {
      Notify.success({ title: 'Intent was enabled successfully!' });
    } else {
      Notify.warning({ title: `Intent ${name} was disabled!` });
    }
  };

  handleMergeImport = () => {
    this.props.history.push(`${this.props.match.url}/import/merge`);
  };

  handleReplaceImport = () => {
    this.props.history.push(`${this.props.match.url}/import/replace`);
  };

  handlePartialExport = () => {
    this.props.partialExport();
  };

  renderInspect = () => {
    const { selectedTabIndex } = this.state;
    const {
      list,
      location,
      onAnnotatorExecute,
      onListItemDeselect,
    } = this.props;

    const match = matchPath(location.pathname, {
      exact: true,
      path: '/:company/:project/intents/:intent',
    });

    if (match && list.length > 0) {
      return (
        <Inspect
          changeTab={this.handleChangeTab}
          closeTabs={onListItemDeselect}
          executeAnnotator={onAnnotatorExecute}
          id={match.params.intent}
          selectedTabIndex={selectedTabIndex}
          onClose={() => {
            //  Use this.props.inspectIsDirty and not inspectIsDirty bellow otherwise it dones not work
            if (this.props.inspectIsDirty) {
              const onDiscardCallback = () => {
                onListItemDeselect();
                this.closeUnsavedChangesModal();
              };

              this.openUnsavedChangesModal(onDiscardCallback);
            } else {
              onListItemDeselect();
            }
          }}
          onItemDeselect={onListItemDeselect}
        />
      );
    }

    return null;
  };

  renderList = () => {
    const {
      list,
      listIsLoading,
      listPagination,

      inspectingItem,
      inspectingItemIndex,

      onListItemDeselect,
    } = this.props;

    if (listIsLoading) {
      return <Preloader />;
    }

    if (list.length > 0) {
      return (
        <List
          currentIntentIndex={inspectingItemIndex}
          deselectIntent={onListItemDeselect}
          duplicateIntent={this.handleListItemDuplicate}
          intents={list}
          selectedIntent={inspectingItem}
          selectIntent={this.handleListItemSelect}
          toggleIntentStatus={this.handleListItemToggleStatus}
        />
      );
    }

    if (listPagination.name__icontains !== '') {
      return (
        <UnitsSearchResult
          items={list}
          searchTerm={listPagination.name__icontains}
          showAll={this.fetchAllItems}
          unitName="intents"
        />
      );
    }

    return (
      <NoUnits openModal={this.handleOpenCreateModal} unitName="intents" />
    );
  };

  renderModals = () => {
    const { isCreateModalOpen, toDuplicate } = this.state;

    return (
      isCreateModalOpen && (
        <CreateModal
          initialValues={
            toDuplicate && {
              intentName: toDuplicate.name,
              description: toDuplicate.description,
              constraintSets: toDuplicate.constraintSets,
            }
          }
          onCheckIntentName={this.props.onCheckIntentName}
          onClose={this.handleCloseCreateModal}
          onSubmit={this.handleListItemAdd}
        />
      )
    );
  };

  render() {
    const {
      project,
      list,
      listCount,
      listSort,
      listSortSelected,
      listFilter,
      listPagination,

      annotatorSelectedResult,
      annotatorHasSelectedResult,

      onListGet,
      onAnnotatorDeselect,

      isTraining,
      isTrainingError,
      hasMachineLearningFeatureEnabled,
    } = this.props;

    const { trainingModalIsOpen, unsavedChangesModal } = this.state;

    const { title, contents, primaryAction } = getTrainModalContents(
      trainingModalIsOpen,
      isTraining,
      isTrainingError,
      this.toggleIsTrainingModal,
    );

    return (
      <Splitter split="vertical">
        <TestSets />

        <HotKeys
          handlers={this.handleHotKeys}
          keyMap={HOTKEYS_MAP}
          styleName="root"
        >
          {this.renderModals()}

          <Modal
            data-test="train-indicator-modal"
            open={trainingModalIsOpen}
            primaryAction={primaryAction}
            title={title}
          >
            {contents}
          </Modal>

          {unsavedChangesModal.isOpen && (
            <UnsavedChangesModal
              isOpen
              onCancel={this.closeUnsavedChangesModal}
              onDiscard={unsavedChangesModal.onDiscardCallback}
            />
          )}

          <ListHeaderOptions
            bottomRightOptions={
              <React.Fragment>
                <FilterMenu
                  filterOptions={listFilter}
                  modelName="entity"
                  onFilter={this.handleFilter}
                />

                <SortingMenu
                  getItems={onListGet}
                  iconClassName="icon-sorting-az"
                  items={list}
                  options={listSort}
                  selectedSortOption={listSortSelected}
                />
              </React.Fragment>
            }
            displayTrainButton={hasMachineLearningFeatureEnabled}
            getItems={onListGet}
            items={list}
            modelName="intent"
            openNewModal={this.handleOpenCreateModal}
            searchTerm={listPagination.name__icontains}
            onExport={this.handlePartialExport}
            onMergeImport={this.handleMergeImport}
            onReplaceImport={this.handleReplaceImport}
            onTrain={() => {
              this.toggleIsTrainingModal();
              this.props.train();
            }}
          />

          {annotatorHasSelectedResult && (
            <WinningPanel
              currentProject={project}
              deselectAnnotatorResult={onAnnotatorDeselect}
              selectedAnnotatorResult={annotatorSelectedResult}
            />
          )}

          <div className="tabs-content right-tabs height-184">
            <Splitter split="horizontal">
              {this.renderList()}
              {this.renderInspect()}
            </Splitter>
          </div>

          {list.length > 0 && (
            <Pagination
              className="footer__pagination"
              count={listCount}
              getItems={onListGet}
              paginationOptions={listPagination}
            />
          )}
        </HotKeys>
      </Splitter>
    );
  }
}

const mapStateToProps = state => ({
  // PROJECT:
  isExporting: selectors.getIsExporting(state),
  project: projectSelectors.getProject(state),
  isTraining: selectors.getIsTraining(state),
  isTrainingError: selectors.getIsTrainingError(state),
  hasMachineLearningFeatureEnabled: projectSelectors.getHasMachineLearningEnabled(
    state,
  ),

  // COLORS:
  colors: colorsSelectors.getColors(state),

  // LIST:
  list: selectors.getIntentsList(state),
  listIsLoading: selectors.getIsLoadingList(state),
  listPagination: selectors.getPaginationOptions(state),
  listCount: selectors.getIntentsListCount(state),

  listSort: selectors.getFilteredSortOptions(state),
  listSortSelected: selectors.getSelectedSortOption(state),

  listFilter: selectors.getFilterOptions(state),

  // INSPECTING:
  inspectingItem: selectors.getIntent(state),
  inspectingItemIndex: selectors.getIntentIndex(state),
  inspectIsDirty: isDirty(FORM_NAMES.INTENTS.INSPECT)(state),
  inspectingUpdates: getFormValues(FORM_NAMES.INTENTS.INSPECT)(state),

  // ANNOTATOR:
  annotatorHasSelectedResult: annotationsSelectors.getHasSelectedAnnotatorResult(
    state,
  ),
  annotatorSelectedResult: annotationsSelectors.getSelectedAnnotatorResult(
    state,
  ),
});

const mapDispatchToProps = {
  cancelExport: actions.cancelExportIntents,
  onListGet: actions.getIntentsList,
  onListItemAdd: actions.addIntent,
  onListItemSelect: actions.selectIntent,
  onListItemDeselect: actions.deselectIntent,
  onListItemPartialUpdate: actions.partialUpdateIntent,
  onCheckIntentName: actions.checkIntentName,
  train: actions.train,
  saveUtterances,
  onAnnotatorExecute: annotationsActions.annotate,
  onAnnotatorDeselect: annotationsActions.deselectAnnotatorResult,
  partialExport: actions.partialExportIntents,
  updateIntent: actions.updateIntent,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withPartialImportModal('intents'),
  withExportingModal('Intents'),
)(Intents);
